import { FC, useMemo } from 'react';
import { Tooltip } from 'react-tooltip';

import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import {
  IncentiveOfferOperation,
  IncentiveOfferType,
} from 'modules/incentivesMarket/types';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import { TablePagination } from 'modules/_shared/Table/components/TablePagination';

import { hasInternalView } from 'utils/showDetails';
import {
  getOperationsPageDocsSummary,
  getSummaryBadgeClass,
} from 'modules/incentivesMarket/utils/operationDocsHelpers';
import { IncentiveOfferOperationsSummary } from '../types/IncentiveOfferDetailContextType';
import IncentiveOfferOperationsTableRow from './IncentiveOfferOperationsTableRow';

const PAGE_DOCS_SUMMARY_TOOLTIP_ID = 'operations-page-docs-summary-tooltip';

/* eslint-disable no-unused-vars -- callback param names in type definitions */
type IncentiveOfferOperationsTableProps = {
  operations: IncentiveOfferOperation[];
  offerType: IncentiveOfferType;
  page: number;
  rowsPerPage: number;
  totalRows: number;
  summary: IncentiveOfferOperationsSummary;
  isLoading?: boolean;
  isDownloading?: boolean;
  onGoToOperation: (operationId: string) => void;
  onOpenGenerateContractModal?: (operation: IncentiveOfferOperation) => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onDownload: () => void;
};
/* eslint-enable no-unused-vars */

const IncentiveOfferOperationsTable: FC<IncentiveOfferOperationsTableProps> = ({
  operations,
  offerType,
  page,
  rowsPerPage,
  totalRows,
  summary,
  isLoading = false,
  isDownloading = false,
  onGoToOperation,
  onOpenGenerateContractModal,
  onPageChange,
  onRowsPerPageChange,
  onDownload,
}) => {
  const { t } = useTranslate();

  const pageDocsSummary = useMemo(
    () => getOperationsPageDocsSummary(operations),
    [operations]
  );

  if (totalRows === 0 && !isLoading) return null;

  const isBuyOffer = offerType === IncentiveOfferType.BUY;
  const showPagination = totalRows > rowsPerPage;
  const showLoadingOverlay = isLoading || isDownloading;

  const signatureBadgeClass = getSummaryBadgeClass(
    pageDocsSummary.signatures.existing,
    pageDocsSummary.signatures.total
  );
  const contractBadgeClass = getSummaryBadgeClass(
    pageDocsSummary.contracts.existing,
    pageDocsSummary.contracts.total
  );

  return (
    <div
      className="card card-bordered mb-4"
      data-testid="incentive-offer-operations-table"
    >
      <div className="card-inner">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div className="d-flex flex-wrap align-items-center mb-0">
            <h6 className="card-title mb-0">
              <em className="icon ni ni-list-check text-primary mr-2" />
              {t('OperationStatus')}
            </h6>
            <span
              className="d-inline-flex align-items-center mx-1"
              data-testid="operations-page-docs-summary"
              data-tooltip-id={PAGE_DOCS_SUMMARY_TOOLTIP_ID}
            >
              <span
                className={`badge badge-dim mx-1 ${signatureBadgeClass}`}
                data-testid="operations-page-signatures-summary"
              >
                {pageDocsSummary.signatures.existing}/
                {pageDocsSummary.signatures.total} {t('Signatures')}
              </span>

              <span
                className={`badge badge-dim mx-1 ${contractBadgeClass}`}
                data-testid="operations-page-contracts-summary"
              >
                {pageDocsSummary.contracts.existing}/
                {pageDocsSummary.contracts.total} {t('Contracts')}
              </span>
            </span>
            <Tooltip
              id={PAGE_DOCS_SUMMARY_TOOLTIP_ID}
              place="top"
              style={{ zIndex: 999 }}
            >
              {t('OperationsPageDocsSummaryTooltip')}
            </Tooltip>
          </div>
          <Button
            variant={ButtonVariants.LIGHT}
            onClick={onDownload}
            disabled={showLoadingOverlay || totalRows === 0}
            title={t('DownloadList')}
            data-testid="incentive-offer-operations-download-button"
            className="mb-2 mb-md-0"
          >
            <em className="icon ni ni-download mr-1" />
            <span>{t('DownloadList')}</span>
          </Button>
        </div>

        <p className="text-soft fs-13px mb-3">
          {t('OperationsTableDescription')}
        </p>

        <div className="table-responsive">
          <table className="table table-tranx table-hover mb-0">
            <thead>
              <tr className="tb-tnx-head">
                <th className="tb-tnx-id">
                  <span className="d-none d-md-inline">
                    {t('OperationRef')}
                  </span>
                </th>
                <th>
                  <span className="d-none d-md-inline">
                    {t(isBuyOffer ? 'Seller' : 'Buyer')}
                  </span>
                </th>
                <th>
                  <span className="d-none d-md-inline">
                    {t('IncentivesMarketUnits')}
                  </span>
                </th>

                <th>
                  <span className="d-none d-md-inline">{t('Amount')}</span>
                </th>
                <th>
                  <span className="d-none d-md-inline">{t('Date')}</span>
                </th>
                <th>
                  <span className="d-none d-md-inline text-center">
                    {t('Docs')}
                  </span>
                </th>
                <th>
                  <span className="d-none d-md-inline">{t('Status')}</span>
                </th>
                <th className="tb-tnx-action text-right" />
              </tr>
            </thead>
            <tbody>
              {operations.map((operation, index) => (
                <IncentiveOfferOperationsTableRow
                  key={operation._id}
                  index={index}
                  operation={operation}
                  isBuyOffer={isBuyOffer}
                  onGoToOperation={onGoToOperation}
                  onOpenGenerateContractModal={onOpenGenerateContractModal}
                />
              ))}
            </tbody>
          </table>
        </div>

        {showPagination && (
          <TablePagination
            key={`${page}-${rowsPerPage}`}
            totalRows={totalRows}
            defaultPage={page}
            defaultRowsPerPage={rowsPerPage}
            onChangePage={onPageChange}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        )}

        <div className="mt-3 pt-3 border-top">
          <p className="text-soft fs-12px mb-0">
            <em className="icon ni ni-info mr-1" />
            <span>
              {t('TotalOperations')}: <strong>{totalRows}</strong> ·{' '}
            </span>
            <span>
              {t('Completed')}: {summary.completed} ·{' '}
            </span>
            <span>
              {t('Pending')}: {summary.pending} ·{' '}
            </span>
            {hasInternalView() && (
              <span>
                {t('IncentivesMarketTotalUnits')}:{' '}
                <strong>{formatNumber(summary.totalUnits)}</strong> ·{' '}
              </span>
            )}
            {hasInternalView() && (
              <span>
                {t('TotalOperatedAmount')}:{' '}
                <strong>{formatCurrencyDecimals(summary.totalAmount)}</strong>
              </span>
            )}
          </p>
        </div>
      </div>

      {showLoadingOverlay && <CustomLoading />}
    </div>
  );
};

export default IncentiveOfferOperationsTable;
